import {
    AgGrid,
    AgGridAutoRefresh,
    AgGridColumnMenuTrigger,
    AgGridExportMenu,
    AgGridPagination,
    AgGridProvider,
    AgGridToggleShowFilters,
    NyUppercaseInput
} from 'nyse-web-tools-common/lib/react';
import connect from 'nyse-web-tools-common/lib/utils/redux.connect';
import {useSnackbar} from 'notistack';
import React, {useMemo, useState} from 'react';

import {DeleteConfirmationModal} from '../components/modals/symbolMpidClearing/deleteConfirmation.modal';
import {AddUpdateSymbolMpidClearingModal} from '../components/modals/symbolMpidClearing/addUpdate.modal';
import {FirmSelect} from '../components/selects/firm.select';
import {FirmParticipantSelect} from '../components/selects/firmParticipant.select';
import {SymbolMpidClearingPermissions} from '../constants/permissions';
import {setColumns} from '../stores/columnDefs/columnDefs.actions';
import {createSymbolMpidClearing, deleteSymbolMpidClearing, getSymbolMpidClearing} from '../stores/symbolMpidClearing/symbolMpidClearing.actions';
import {hasPermissions} from '../utils/utilities';

const SymbolMpidClearingPageComponent = (props) => {
    const {enqueueSnackbar} = useSnackbar();

    const [filterValues, setFilterValues] = useState({});
    const [selectedRows, setSelectedRows] = useState([]);
    const [capturedRows, setCapturedRows] = useState([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showAddUpdateModal, setShowAddUpdateModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);

    const initialPagination = useMemo(() => ({
        limit: 500,
        offset: 0
    }), []);

    const initialFilters = useMemo(() => ({
        userCrd: props.route.params.crd || (props.firms[0] || {}).crd,
        mpid: '',
        symbol: '',
        clearingNumber: ''
    }), [props.route.params.crd, props.firms]);

    const handleFirmChange = ({setFieldValue}) => {
        setFieldValue('mpid', '');
        setFieldValue('symbol', '');
        setFieldValue('clearingNumber', '');
    };

    const handleFiltersChanged = (newFilterValues) => {
        setFilterValues(newFilterValues);
    };

    const handleSelectionChanged = (newSelectedRows) => {
        setSelectedRows(newSelectedRows);
    };

    const handleAddClick = () => {
        setShowAddUpdateModal(true);
        setIsEditMode(false);
    };

    const handleEditClick = (event) => {
        // Prevent button click from affecting grid selection
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        if (selectedRows.length === 0) {
            return;
        }
        if (selectedRows.length > 1000) {
            enqueueSnackbar(
                'Maximum 1,000 records can be selected for edit. Please reduce your selection.',
                {
                    variant: 'error',
                    autoHideDuration: 5000
                }
            );
            return;
        }
        setCapturedRows([...selectedRows]);
        setIsEditMode(true);
        setShowAddUpdateModal(true);
    };

    const handleDeleteClick = (event) => {
        // Prevent button click from affecting grid selection
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        if (selectedRows.length === 0) {
            return;
        }
        if (selectedRows.length > 1000) {
            enqueueSnackbar(
                'Maximum 1,000 records can be selected for delete. Please reduce your selection.',
                {
                    variant: 'error',
                    autoHideDuration: 5000
                }
            );
            return;
        }
        // Capture the selected rows before opening modal (grid selection may be cleared)
        setCapturedRows([...selectedRows]);
        setShowDeleteModal(true);
    };

    const handleModalClose = (isSuccessful, successMessage) => {
        setShowDeleteModal(false);
        setShowAddUpdateModal(false);
        setCapturedRows([]);
        setSelectedRows(isSuccessful ? [] : selectedRows);

        if (isSuccessful) {
            // Show success notification with message from API
            enqueueSnackbar(
                successMessage || 'Symbol MPID clearing configuration(s) saved successfully',
                {
                    variant: 'success',
                    autoHideDuration: 3000
                }
            );
            // Refresh grid
            props.getSymbolMpidClearing(filterValues);
        }
    };

    const handleAddUpdateModalHide = () => {
        setShowAddUpdateModal(false);
    };

    const handleDeleteModalHide = () => {
        setShowDeleteModal(false);
    };

    const hasPermissionCreate = hasPermissions(
        props.user,
        [SymbolMpidClearingPermissions.create],
        [props.platform.mic]
    );
    const hasPermissionDelete = hasPermissions(
        props.user,
        [SymbolMpidClearingPermissions.delete],
        [props.platform.mic]
    );

    // Extract only grid-relevant props to prevent unnecessary reloads
    const gridProps = useMemo(() => ({
        isLoading: props.symbolMpidClearing.isLoading,
        error: props.symbolMpidClearing.error,
        rowData: props.symbolMpidClearing.rowData,
        more: props.symbolMpidClearing.more,
        totalCount: props.symbolMpidClearing.totalCount
    }), [
        props.symbolMpidClearing.isLoading,
        props.symbolMpidClearing.error,
        props.symbolMpidClearing.rowData,
        props.symbolMpidClearing.more,
        props.symbolMpidClearing.totalCount
    ]);

    return (
        <AgGridProvider
            columnNamespace='symbolMpidClearing'
            columnDefs={props.columnDefs.symbolMpidClearing}
            filtersClassName='row space-bottom-1'
            initialFilters={initialFilters}
            initialPagination={initialPagination}
            onFiltersChanged={handleFiltersChanged}
            onLoad={props.getSymbolMpidClearing}
            onColumnsChanged={props.setColumns}
            onSelectionChanged={handleSelectionChanged}
            rowSelection='multiple'
            submitOnLoad={true}
            {...gridProps}>
            <AgGridProvider.Filters>
                {props.firms.length > 1 && (
                    <div className='form-group col-lg-2 col-md-3 col-sm-6 col-xs-12'>
                        <label>Firm</label>
                        <FirmSelect
                            initialValue={props.route.params.crd}
                            name='userCrd'
                            onChange={handleFirmChange}
                            required={true}
                        />
                    </div>
                )}
                <div className='form-group col-lg-2 col-md-3 col-sm-6 col-xs-12'>
                    <label>MPID</label>
                    <FirmParticipantSelect
                        crd={filterValues.userCrd}
                        disableInactiveControl
                        hideEtpid
                        name='mpid'
                        required={true}
                        autoSelectFirst={true}
                    />
                </div>
                <div className='form-group col-lg-2 col-md-3 col-sm-6 col-xs-12'>
                    <label>Symbol</label>
                    <NyUppercaseInput
                        name='symbol'
                        maxLength={24}
                    />
                </div>
                <div className='form-group col-lg-2 col-md-3 col-sm-6 col-xs-12'>
                    <label>Clearing Number</label>
                    <NyUppercaseInput
                        name='clearingNumber'
                        maxLength={5}
                    />
                </div>
            </AgGridProvider.Filters>
            <div className='row space-bottom-1'>
                <div className='col-md-12 col-lg-12 col-xl-12 col-sm-12 col-xs-12'>
                    <div className='flex-row flex-align-bottom'>
                        <AgGridProvider.SubmitBtn>
                            Search
                        </AgGridProvider.SubmitBtn>
                        <AgGridProvider.ResetBtn>
                            Reset
                        </AgGridProvider.ResetBtn>

                        <div className='btn-circle-grp flex-pull-right flex-row'>
                            {hasPermissionCreate && (
                                <button
                                    className='btn btn-primary space-right-1'
                                    onClick={handleAddClick}>
                                    Add
                                </button>
                            )}

                            {hasPermissionCreate && (
                                <button
                                    className='btn btn-info space-right-1'
                                    onClick={handleEditClick}
                                    disabled={selectedRows.length === 0}>
                                    Edit
                                </button>
                            )}

                            {hasPermissionDelete && (
                                <button
                                    className='btn btn-danger space-right-1'
                                    onClick={handleDeleteClick}
                                    disabled={selectedRows.length === 0}>
                                    Delete
                                </button>
                            )}

                            <AgGridAutoRefresh />
                            <AgGridToggleShowFilters />
                            <AgGridExportMenu
                                exportFileName='symbolMpidClearing'
                                exportSheetName={props.platform.name}
                            />
                            <AgGridColumnMenuTrigger />
                        </div>
                    </div>
                </div>
            </div>

            <AgGrid reactNext={true} />

            <AgGridPagination>
                <option value={100}>100</option>
                <option value={500}>500</option>
                <option value={1000}>1,000</option>
                <option value={5000}>5,000</option>
                <option value={10000}>10,000</option>
            </AgGridPagination>

            {showDeleteModal && (
                <DeleteConfirmationModal
                    userCrd={filterValues.userCrd}
                    selectedRows={capturedRows}
                    show={showDeleteModal}
                    hideModal={handleDeleteModalHide}
                    onClose={handleModalClose}
                />
            )}

            {showAddUpdateModal && (
                <AddUpdateSymbolMpidClearingModal
                    userCrd={filterValues.userCrd}
                    firms={props.firms}
                    isEditMode={isEditMode}
                    existingRows={isEditMode ? capturedRows : []}
                    show={showAddUpdateModal}
                    hideModal={handleAddUpdateModalHide}
                    createSymbolMpidClearing={props.createSymbolMpidClearing}
                    onClose={handleModalClose}
                />
            )}
        </AgGridProvider>
    );
};

export const SymbolMpidClearingPage = connect(
    ['firms', 'columnDefs', 'symbolMpidClearing', 'platform', 'route', 'user'],
    {
        setColumns,
        getSymbolMpidClearing,
        createSymbolMpidClearing,
        deleteSymbolMpidClearing
    }
)(SymbolMpidClearingPageComponent);
